import React, { useContext, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  InputAdornment,
  TextField,
  Typography,
  Switch,
} from "@mui/material";
import { getAmountWithTxnFee, isCostPlusFeesModel } from "../util";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import currency from "currency.js";
import CloseIcon from "@mui/icons-material/Close";
import { UserContext } from "../../context/UserContext";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export default function Price(props) {
  const { amount, includeFees } = props.value;
  const { user } = useContext(UserContext);

  const [open, setOpen] = useState(false);

  /** badInput: the field holds unparsable text (e.g. "abc"), which reads back as an empty value. */
  const handleChange = (amount, includeFees, badInput = false) => {
    const total = includeFees ? getAmountWithTxnFee(amount, user.config?.fees) : amount;
    props.onChange({ amount, includeFees, total, badInput });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <TextField
            id="amount"
            type="number"
            value={amount}
            label={props.label || "Amount"}
            name="amount"
            variant="outlined"
            fullWidth
            required={props.required !== false}
            onChange={(e) =>
              handleChange(e.target.value, includeFees, Boolean(e.target.validity?.badInput))
            }
            onWheel={(e) => {
              e.target && e.target.blur();
            }}
            sx={{
              minWidth: "10rem",
              "& .MuiOutlinedInput-root": {
                backgroundColor: props.disabled ? "#F7F7F7" : "#FFFFFF",
                paddingLeft: "4px",
                paddingRight: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ width: "0.85rem" }}>
                  <AttachMoneyOutlinedIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <Box sx={{ minWidth: "4rem", textAlign: "right" }}>
                  {includeFees && +amount > 0 && (
                    <Box sx={{ textAlign: "right", borderLeft: "1px solid #DDD" }}>
                      <Link
                        onClick={(e) => !isCostPlusFeesModel(user.config?.fees) && setOpen(true)}
                        color="inherit"
                        underline="none"
                        sx={{ cursor: "pointer" }}
                      >
                        <Typography sx={{ fontSize: "0.8rem", lineHeight: "1" }}>
                          {currency(getAmountWithTxnFee(amount, user.config?.fees)).format()}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ fontSize: "0.65rem" }}
                        >
                          with fees
                        </Typography>
                      </Link>
                    </Box>
                  )}
                </Box>
              ),
            }}
            disabled={props.disableAmount}
            helperText=""
          />
        </Grid>
      </Grid>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth={true} fullScreen>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={includeFees}
                onChange={(e) => handleChange(amount, e.target.checked)}
              />
            }
            label="Add Transaction Fees"
          />
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box py={4}>
            {includeFees ? (
              <Box>
                {+amount > 0 && (
                  <Box sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <Typography variant="subtitle1">{currency(amount).format()}</Typography>
                    <Typography variant="h4" sx={{ fontWeight: "700" }}>
                      &#8674; {currency(getAmountWithTxnFee(amount, user.config?.fees)).format()}
                    </Typography>
                  </Box>
                )}
                <Box py={2}>
                  <Typography variant="body1" color="textSecondary">
                    You've chosen to add transaction fees to the amount you've entered. Customer
                    will see & pay the final amount shown above.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" color="textSecondary">
                  Enable the switch above to include your transaction fees in the price shown to the
                  customer to help you offset your transaction cost.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
